import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { CarrierAgreementPenaltyRow } from '../../../store/api/carrierAgreementsApi';
import type { RouteExpense } from '../../../store/api/financeApi';
import type { RouteCompletionDraft } from '../types';
import type { CompletionExpenseDraft } from '../types';
import {
  buildInitialCompletionDraft,
  createEmptyExpenseDraft,
  patchCompletionExpense,
  patchCompletionExpenses,
  patchCompletionLogistComment,
  patchCompletionPenalty,
  penaltyConfigKey
} from './completionDraft';

/**
 * Stable completion draft — re-initializes only when modal opens or penalty config set changes.
 * Fixes checkbox flicker from useEffect([manualPenalties]) array identity loop.
 */
export function useRouteCompletionDraft(params: {
  open: boolean;
  penaltyConfigs: CarrierAgreementPenaltyRow[];
  expenses?: RouteExpense[];
}): {
  draft: RouteCompletionDraft;
  setPenaltyEnabled: (penaltyId: number, enabled: boolean) => void;
  patchPenaltyField: (
    penaltyId: number,
    patch: Partial<{ days: number | null; amount: number | null; comment: string }>
  ) => void;
  setLogistComment: (comment: string) => void;
  addExpense: () => void;
  patchExpense: (index: number, patch: Partial<CompletionExpenseDraft>) => void;
  removeExpense: (index: number) => void;
  resetDraft: () => void;
} {
  const { open, penaltyConfigs, expenses } = params;
  const configKey = useMemo(() => penaltyConfigKey(penaltyConfigs), [penaltyConfigs]);
  const lastInitKey = useRef<string | null>(null);

  const [draft, setDraft] = useState<RouteCompletionDraft>(() =>
    buildInitialCompletionDraft({ penaltyConfigs: [], expenses: [] })
  );

  const resetDraft = useCallback(() => {
    setDraft(buildInitialCompletionDraft({ penaltyConfigs, expenses }));
    lastInitKey.current = configKey;
  }, [penaltyConfigs, expenses, configKey]);

  useEffect(() => {
    if (!open) {
      lastInitKey.current = null;
      return;
    }
    if (lastInitKey.current === configKey) return;
    setDraft(buildInitialCompletionDraft({ penaltyConfigs, expenses }));
    lastInitKey.current = configKey;
  }, [open, configKey, penaltyConfigs, expenses]);

  const setPenaltyEnabled = useCallback((penaltyId: number, enabled: boolean) => {
    setDraft((prev) => patchCompletionPenalty(prev, penaltyId, { enabled }));
  }, []);

  const patchPenaltyField = useCallback(
    (
      penaltyId: number,
      patch: Partial<{ days: number | null; amount: number | null; comment: string }>
    ) => {
      setDraft((prev) => patchCompletionPenalty(prev, penaltyId, patch));
    },
    []
  );

  const setLogistComment = useCallback((comment: string) => {
    setDraft((prev) => patchCompletionLogistComment(prev, comment));
  }, []);

  const addExpense = useCallback(() => {
    setDraft((prev) => patchCompletionExpenses(prev, [...prev.expenses, createEmptyExpenseDraft()]));
  }, []);

  const patchExpense = useCallback((index: number, patch: Partial<CompletionExpenseDraft>) => {
    setDraft((prev) => patchCompletionExpense(prev, index, patch));
  }, []);

  const removeExpense = useCallback((index: number) => {
    setDraft((prev) => {
      const target = prev.expenses[index];
      if (target && !target.isNew) return prev;
      return patchCompletionExpenses(
        prev,
        prev.expenses.filter((_, i) => i !== index)
      );
    });
  }, []);

  return {
    draft,
    setPenaltyEnabled,
    patchPenaltyField,
    setLogistComment,
    addExpense,
    patchExpense,
    removeExpense,
    resetDraft
  };
}
